import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedCaterer, setSelectedCaterer] = useState(null);

  const normalizePricePerPerson = (price) => {
    if (price === null || price === undefined) return null;
    if (typeof price === 'number') return Number.isFinite(price) ? price : null;
    if (typeof price === 'string') {
      const parsed = parseFloat(price.replace(/[^0-9.]/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('mrscater_cart');
    const savedCaterer = localStorage.getItem('mrscater_cart_caterer');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        const normalizedCart = Array.isArray(parsedCart)
          ? parsedCart.map((item) => ({
              ...item,
              pricePerPerson: normalizePricePerPerson(item.pricePerPerson)
            }))
          : [];
        setCartItems(normalizedCart);
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
    if (savedCaterer) {
      try {
        setSelectedCaterer(JSON.parse(savedCaterer));
      } catch (e) {
        console.error('Error loading caterer:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mrscater_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (selectedCaterer) {
      localStorage.setItem('mrscater_cart_caterer', JSON.stringify(selectedCaterer));
    }
  }, [selectedCaterer]);

  /** Add item to cart. addons = [{ id, name, price, quantity }] (optional). */
  const addToCart = (item, caterer, quantity = 1, addons = null) => {
    // Check if adding from a different caterer
    if (selectedCaterer && selectedCaterer.id !== caterer.id && cartItems.length > 0) {
      const confirmClear = window.confirm(
        `Your cart contains items from ${selectedCaterer.name}. Do you want to clear the cart and add items from ${caterer.name}?`
      );
      if (!confirmClear) return false;
      setCartItems([]);
    }

    setSelectedCaterer({ ...caterer, deliveryFee: caterer.deliveryFee ?? caterer.delivery_fee ?? 0 });

    const normalizedPrice = normalizePricePerPerson(item.pricePerPerson);
    const normalizedAddons = Array.isArray(addons) ? addons.filter(a => a && (a.quantity || 0) > 0) : null;

    setCartItems(prev => {
      const existingIndex = prev.findIndex(cartItem => {
        if (cartItem.id !== item.id) return false;
        const sameAddons = JSON.stringify(cartItem.addons || []) === JSON.stringify(normalizedAddons || []);
        return sameAddons;
      });

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }
      return [...prev, {
        ...item,
        pricePerPerson: normalizedPrice,
        quantity,
        catererId: caterer.id,
        addons: normalizedAddons && normalizedAddons.length > 0 ? normalizedAddons : undefined
      }];
    });

    return true;
  };

  const removeFromCart = (targetItem) => {
    setCartItems(prev => {
      const filtered = prev.filter(item => item !== targetItem);
      if (filtered.length === 0) {
        setSelectedCaterer(null);
        localStorage.removeItem('mrscater_cart_caterer');
      }
      return filtered;
    });
  };

  const updateQuantity = (targetItem, quantity) => {
    if (quantity < 1) {
      removeFromCart(targetItem);
      return;
    }

    setCartItems(prev =>
      prev.map(item =>
        item === targetItem ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setSelectedCaterer(null);
    localStorage.removeItem('mrscater_cart');
    localStorage.removeItem('mrscater_cart_caterer');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = Number.isFinite(item.pricePerPerson) ? item.pricePerPerson : 0;
      const quantity = Math.max(1, item.quantity || 1);
      let lineTotal = price * quantity;
      if (item.addons && item.addons.length) {
        item.addons.forEach((a) => {
          lineTotal += (Number(a.price) || 0) * (Number(a.quantity) || 1);
        });
      }
      return total + lineTotal;
    }, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const getTotalGuests = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  /** Delivery fee from selected caterer (ZAR) */
  const getDeliveryFee = () => {
    if (!selectedCaterer) return 0;
    const fee = selectedCaterer.deliveryFee ?? selectedCaterer.delivery_fee;
    return Number.isFinite(fee) ? fee : 0;
  };

  /** Subtotal + 5% service fee + delivery fee */
  const getTotalWithFees = () => {
    const subtotal = getCartTotal();
    const serviceFee = subtotal * 0.05;
    const deliveryFee = getDeliveryFee();
    return subtotal + serviceFee + deliveryFee;
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(prev => !prev);

  const value = {
    cartItems,
    selectedCaterer,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    getTotalGuests,
    getDeliveryFee,
    getTotalWithFees,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
